import { useState } from 'react';
import { describeError } from '../api/errors';
import { useAddressRequests, useDecideAddressRequest } from '../api/hooks';
import type { AddressRequestRow, AddressShape } from '../api/types';
import { Alert, Button, Chip, Field, Panel } from '../ui';

const TONE = { PENDING: 'us', APPROVED: 'done', REJECTED: 'stop' } as const;
const LABEL = { PENDING: 'Pending approval', APPROVED: 'Approved', REJECTED: 'Rejected' } as const;
const KIND = { REGISTERED: 'Registered address', BILLING: 'Billing address', SHIPPING: 'Delivery address' } as const;

const ROWS: Array<[keyof AddressShape, string]> = [
  ['contactName', 'Contact name'], ['line1', 'Address line 1'], ['line2', 'Address line 2'], ['city', 'City'],
  ['postalCode', 'Postal code'], ['countryCode', 'Country'], ['contactPhone', 'Phone'],
];

/**
 * The approved address beside the requested one, one named row per field and only the fields either
 * address has. A row that changes is tinted and its new value is bold, so what to approve is the tinted rows.
 */
function Compare({ current, proposed }: { current: AddressShape | null; proposed: AddressShape | null }) {
  const rows = ROWS.filter(([k]) => current?.[k] || proposed?.[k]);
  const cell = { padding: '5px 10px' } as const;
  return (
    <div
      style={{
        display: 'grid', gridTemplateColumns: 'minmax(96px, 130px) 1fr 1fr', marginTop: 'var(--mk-space-2)',
        border: '1px solid var(--mk-st-us-rule)', borderRadius: 'var(--mk-radius-card)', overflow: 'hidden', fontSize: 13,
      }}
    >
      <div className="mk-dim" style={{ ...cell, fontSize: 11.5, textTransform: 'uppercase' }}>Field</div>
      <div className="mk-dim" style={{ ...cell, fontSize: 11.5, textTransform: 'uppercase' }}>{current ? 'Current approved' : 'Current (none)'}</div>
      <div className="mk-dim" style={{ ...cell, fontSize: 11.5, textTransform: 'uppercase' }}>Requested</div>
      {rows.map(([k, label]) => {
        const was = current?.[k] ?? null;
        const now = proposed?.[k] ?? null;
        const changed = was !== now;
        const tint = changed ? { background: 'var(--mk-st-us-bg)' } : {};
        return (
          <div key={k} style={{ display: 'contents' }}>
            <div className="mk-dim" style={{ ...cell, ...tint, borderTop: '1px solid var(--mk-st-us-rule)' }}>{label}</div>
            <div style={{ ...cell, ...tint, borderTop: '1px solid var(--mk-st-us-rule)' }}>{was ?? '—'}</div>
            <div style={{ ...cell, ...tint, borderTop: '1px solid var(--mk-st-us-rule)', fontWeight: changed ? 600 : 400 }}>{now ?? '—'}</div>
          </div>
        );
      })}
    </div>
  );
}

/**
 * A-04 — address changes a retailer has asked for: the approved address beside the requested one, who
 * asked and when, and the decision. The approved address only changes on approval (`retailer.edit`).
 */
export function AddressRequestsPanel(
  { retailerId, canDecide, show }: { retailerId: string; canDecide: boolean; show: 'PENDING' | 'DECIDED' },
) {
  const requests = useAddressRequests(retailerId);
  const decide = useDecideAddressRequest(retailerId);
  const [rejecting, setRejecting] = useState<string | null>(null);
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | null>(null);

  const rows = (requests.data ?? []).filter((r) => (show === 'PENDING') === (r.status === 'PENDING'));
  if (rows.length === 0) return null;

  async function run(r: AddressRequestRow, decision: 'approve' | 'reject') {
    setError(null);
    try {
      await decide.mutateAsync({ requestId: r.id, decision, ...(decision === 'reject' ? { reason } : {}) });
      setRejecting(null); setReason('');
    } catch (e) { setError(describeError(e)); }
  }

  return (
    <Panel title={show === 'PENDING' ? 'Address changes waiting for your approval' : 'Address change history'} pad>
      {error ? <Alert tone="stop">{error}</Alert> : null}
      {rows.map((r) => (
        <div key={r.id} style={{ marginBottom: 'var(--mk-space-5)' }}>
          <div>
            <b>{r.kind ? KIND[r.kind] : 'Address'} change</b>{' '}
            <Chip tone={TONE[r.status]}>{LABEL[r.status]}</Chip>{' '}
            <span className="mk-dim">
              Requested by {r.requestedBy ?? 'the retailer'} on {new Date(r.requestedAt).toLocaleString()}
            </span>
          </div>
          <Compare current={r.current} proposed={r.proposed} />
          {r.status !== 'PENDING' ? (
            <div className="mk-dim">
              {r.status === 'APPROVED' ? 'Approved' : 'Rejected'} by {r.decidedBy ?? 'Miraki'}
              {r.decidedAt ? ` on ${new Date(r.decidedAt).toLocaleString()}` : ''}
              {r.decisionReason ? ` — ${r.decisionReason}` : ''}
            </div>
          ) : canDecide ? (
            rejecting === r.id ? (
              <div style={{ marginTop: 8 }}>
                <Field label="Reason for rejecting (shown to the retailer)">
                  <input className="mk-fld" value={reason} maxLength={300} onChange={(e) => setReason(e.target.value)} />
                </Field>
                <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                  <Button disabled={reason.trim().length < 3 || decide.isPending} onClick={() => void run(r, 'reject')}>Confirm rejection</Button>
                  <Button tone="ghost" onClick={() => { setRejecting(null); setReason(''); }}>Cancel</Button>
                </div>
              </div>
            ) : (
              <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                <Button disabled={decide.isPending} onClick={() => void run(r, 'approve')}>Approve change</Button>
                <Button tone="ghost" disabled={decide.isPending} onClick={() => setRejecting(r.id)}>Reject</Button>
              </div>
            )
          ) : null}
        </div>
      ))}
    </Panel>
  );
}
